import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { InsurancesActions } from 'application/constants/actions';
import { fetchInsurancesProps } from 'common/types/insurances';
import { InsurancesAPI } from 'integration/api/insurances';
import { InsuranceCreationDto, InsuranceUpdateDto } from '@docbay/schemas';

/**
 * Fetches insurances based on the given parameters.
 *
 * @param params - The parameters to fetch the insurances with.
 * @returns The fetched insurances data if successful, or an error message if unsuccessful.
 */
export const fetchInsurances = createAsyncThunk(
  InsurancesActions.fetchInsurances,
  async (params: fetchInsurancesProps, { rejectWithValue }) => {
    try {
      const { data } = await InsurancesAPI.fetchInsurances(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes an insurance based on the given ID.
 *
 * @param id - The ID of the insurance to delete.
 * @returns The deleted insurance data if successful, or an error message if unsuccessful.
 */
export const deleteInsurances = createAsyncThunk(
  InsurancesActions.deleteInsurances,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await InsurancesAPI.deleteInsurances(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Adds a new insurance based on the given parameters.
 *
 * @param params - The parameters to add the new insurance with.
 * @returns The added insurance data if successful, or an error message if unsuccessful.
 */
export const addInsurances = createAsyncThunk(
  InsurancesActions.addInsurances,
  async (params: InsuranceCreationDto, { rejectWithValue }) => {
    try {
      const { data } = await InsurancesAPI.addInsurances(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Edits an existing insurance based on the given parameters.
 *
 * @param params - The parameters to edit the existing insurance with, including the insurance ID and updated data.
 * @returns The edited insurance data if successful, or an error message if unsuccessful.
 */
export const editInsurances = createAsyncThunk(
  InsurancesActions.editInsurances,
  async (
    params: { data: InsuranceUpdateDto; id: string },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await InsurancesAPI.editInsurances(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
